import { useId, useState } from "react";
import { problemMessage } from "@/api/client";
import type { AllocationItem, JobCode } from "@/api/types";
import "./AllocationPanel.css";

export interface AllocationPanelProps {
  /**
   * The entry's own total — a manual submit's typed value, an online punch-out's
   * derived `hours`, or (offline) this device's own best estimate against the shift's
   * already-confirmed start (`TimeClock.tsx`'s `queueLocally`). Whatever it is, it is the
   * number a split has to sum to exactly; a quick pick sends it whole to one code.
   */
  hours: number;
  /** The farm's own active job codes — ef-024's whole point is that this is the SAME
   *  list regardless of which entry opened the panel (the canary this file's own e2e
   *  spec, `worker-allocation.spec.ts` C2, is written against). */
  jobCodes: JobCode[];
  /**
   * Resolves once the choice is durably recorded — a real `PUT /allocations` for an
   * already-submitted row, or a local attach onto a still-queued punch-out
   * (`TimeClock.tsx` decides which). REJECTS on failure; this panel stays open and shows
   * the server's own words rather than closing on an answer that never landed.
   */
  onSave: (allocations: AllocationItem[]) => Promise<void>;
  /** Always legal, always synchronous, never a network call — contract Assumption A8. */
  onSkip: () => void;
}

type Mode = "pick" | "split";

/**
 * ef-024 — "What was this for?" The worker's own counterpart to the owner's `AllocatePanel`
 * in `TimeApprovalQueue.tsx`: same fixed-two-line split, same "show the working" status
 * line, same disabled-until-exact Save — offered the moment a shift closes or an hours
 * entry is filed, rather than found later by an owner going looking for it.
 *
 * ONE-TAP DEFAULT PER CODE, not a single "most likely" suggestion picked for the worker.
 * Every active job code is its own full-hours button — `worker-allocation.spec.ts`'s own
 * canary (C2) picks the THIRD fixture on a SECOND entry and requires it enabled, which
 * rules out "only offer the most recently used code" or any other narrowing.
 */
export function AllocationPanel({ hours, jobCodes, onSave, onSkip }: AllocationPanelProps) {
  const [mode, setMode] = useState<Mode>("pick");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [line1Code, setLine1Code] = useState("");
  const [line1Hours, setLine1Hours] = useState("");
  const [line2Code, setLine2Code] = useState("");
  const [line2Hours, setLine2Hours] = useState("");

  const headingId = useId();
  const jobCodeId1 = useId();
  const jobCodeId2 = useId();
  const hoursId1 = useId();
  const hoursId2 = useId();

  const target = Math.round(hours * 100) / 100;

  const h1 = line1Hours.trim() === "" ? null : Number(line1Hours);
  const h2 = line2Hours.trim() === "" ? null : Number(line2Hours);
  const validH1 = h1 !== null && Number.isFinite(h1) && h1 > 0;
  const validH2 = h2 !== null && Number.isFinite(h2) && h2 > 0;

  const rawSum = (validH1 ? h1! : 0) + (validH2 ? h2! : 0);
  const sum = Math.round(rawSum * 100) / 100;
  const diff = Math.round((target - sum) * 100) / 100;
  // A float-safe "close enough to zero" — same tolerance the owner's own AllocatePanel
  // uses for the identical reason (0.30 + 0.30 does not equal 0.60 in a double).
  const isShort = Math.abs(diff) > 0.004;

  const bothLinesChosen = line1Code !== "" && line2Code !== "";
  const sameCodeTwice = line1Code !== "" && line1Code === line2Code;
  const canSaveSplit =
    !saving && bothLinesChosen && validH1 && validH2 && !sameCodeTwice && !isShort;

  // "Show the working" — `worker-allocation.spec.ts` A3 asserts the CURRENT addend, the
  // entry's own total, the word "remaining" while short, and the exact shortfall, all in
  // this one live region. Mirrors `AllocatePanel`'s own `statusText` construction.
  const h1Text = (validH1 ? h1! : 0).toFixed(2);
  const h2Text = (validH2 ? h2! : 0).toFixed(2);
  const statusText =
    `${h1Text} + ${h2Text} = ${sum.toFixed(2)} of ${target.toFixed(2)} hours allocated` +
    (isShort
      ? diff > 0
        ? ` — ${diff.toFixed(2)} remaining`
        : ` — ${Math.abs(diff).toFixed(2)} over`
      : " — ready to save");

  async function quickPick(jobCode: JobCode): Promise<void> {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave([{ jobCodeId: jobCode.id, hours: target }]);
    } catch (err) {
      setError(problemMessage(err, "Could not save that job code."));
    } finally {
      setSaving(false);
    }
  }

  async function saveSplit(): Promise<void> {
    if (!canSaveSplit) return;
    setSaving(true);
    setError(null);
    try {
      await onSave([
        { jobCodeId: line1Code, hours: h1! },
        { jobCodeId: line2Code, hours: h2! },
      ]);
    } catch (err) {
      setError(problemMessage(err, "Could not save this split."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="time__allocate"
      aria-labelledby={headingId}
      data-state={mode}
    >
      <h2 id={headingId} className="time__heading time__heading--sub">
        What was this for?
      </h2>

      {error && (
        <p role="alert" className="time__allocate-error">
          {error}
        </p>
      )}

      {mode === "pick" ? (
        <>
          {jobCodes.length === 0 ? (
            <p className="time__allocate-empty">
              No job codes yet — ask your employer to add one, or skip for now.
            </p>
          ) : (
            <div className="time__allocate-codes">
              {jobCodes.map((jobCode) => (
                <button
                  key={jobCode.id}
                  type="button"
                  className="btn btn--ghost time__allocate-code"
                  disabled={saving}
                  onClick={() => void quickPick(jobCode)}
                >
                  <span className="time__allocate-code-tag">{jobCode.code}</span>
                  <span className="time__allocate-code-name">{jobCode.name}</span>
                </button>
              ))}
            </div>
          )}

          {jobCodes.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost time__allocate-toggle"
              disabled={saving}
              onClick={() => setMode("split")}
            >
              Split hours
            </button>
          )}
        </>
      ) : (
        <div className="time__allocate-split">
          <div className="time__allocate-line">
            <label className="time__field-label" htmlFor={jobCodeId1}>
              Job code — line 1
            </label>
            <select
              id={jobCodeId1}
              className="time__input"
              value={line1Code}
              disabled={saving}
              onChange={(e) => setLine1Code(e.target.value)}
            >
              <option value="">Choose a job code</option>
              {jobCodes.map((jobCode) => (
                <option key={jobCode.id} value={jobCode.id}>
                  {jobCode.name}
                </option>
              ))}
            </select>
            <label className="time__field-label" htmlFor={hoursId1}>
              Hours — line 1
            </label>
            <input
              id={hoursId1}
              className="time__input time__input--hours"
              type="text"
              inputMode="decimal"
              value={line1Hours}
              disabled={saving}
              onChange={(e) => setLine1Hours(e.target.value)}
            />
          </div>

          <div className="time__allocate-line">
            <label className="time__field-label" htmlFor={jobCodeId2}>
              Job code — line 2
            </label>
            <select
              id={jobCodeId2}
              className="time__input"
              value={line2Code}
              disabled={saving}
              onChange={(e) => setLine2Code(e.target.value)}
            >
              <option value="">Choose a job code</option>
              {jobCodes.map((jobCode) => (
                <option key={jobCode.id} value={jobCode.id}>
                  {jobCode.name}
                </option>
              ))}
            </select>
            <label className="time__field-label" htmlFor={hoursId2}>
              Hours — line 2
            </label>
            <input
              id={hoursId2}
              className="time__input time__input--hours"
              type="text"
              inputMode="decimal"
              value={line2Hours}
              disabled={saving}
              onChange={(e) => setLine2Hours(e.target.value)}
            />
          </div>

          {/* role="status" — the live sum is exactly the ground truth Save Split's
              disabled state reads, same reasoning as the owner's own AllocatePanel
              (ui-breadcrumbs §1). */}
          <p role="status" className="time__allocate-status">
            {statusText}
          </p>

          <div className="time__allocate-actions">
            <button
              type="button"
              className="btn btn--primary time__allocate-save"
              disabled={!canSaveSplit}
              onClick={() => void saveSplit()}
            >
              {saving ? "Saving…" : "Save split"}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={saving}
              onClick={() => setMode("pick")}
            >
              Choose one code instead
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="btn btn--ghost time__allocate-skip"
        disabled={saving}
        onClick={onSkip}
      >
        Skip
      </button>
    </section>
  );
}
